import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { CheckCircle, CreditCard, Bank, House, Warning, ArrowRight } from "@phosphor-icons/react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const API = process.env.REACT_APP_BACKEND_URL;

export default function TenantPay() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("ach_stub");
  const [note, setNote] = useState("");
  const [payerName, setPayerName] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/pay/${token}`);
        setState({ loading: false, data: r.data, error: null });
        if (r.data.balance_cents > 0) setAmount((r.data.balance_cents / 100).toFixed(2));
      } catch (e) {
        setState({ loading: false, data: null, error: e.response?.data?.detail || "Invalid or expired link" });
      }
    })();
  }, [token]);

  const submit = async () => {
    const amt = Math.round((parseFloat(amount) || 0) * 100);
    if (amt <= 0) return toast.error("Enter a payment amount");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/public/pay/${token}/pay`, {
        amount_cents: amt, method, note, payer_name: payerName, payer_email: payerEmail,
      });
      setSuccess(r.data);
      // refresh view
      const v = await axios.get(`${API}/api/public/pay/${token}`);
      setState({ loading: false, data: v.data, error: null });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  if (state.loading) return <Shell><div className="text-slate-500">Loading…</div></Shell>;
  if (state.error) return <Shell><div className="rugged-card p-8 text-center"><Warning size={40} className="text-red-500 mx-auto"/><div className="mt-2 font-black">{state.error}</div><div className="text-xs text-slate-500 mt-1">Contact your property manager for a new link.</div></div></Shell>;

  const d = state.data;
  const brand = d.branding || {};
  return (
    <Shell brand={brand}>
      <Toaster position="top-right" richColors closeButton/>
      <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-start gap-2 mb-4" data-testid="tenant-pay-stub-banner">
        <Warning size={14} className="text-amber-600 mt-0.5"/>
        <span><b>Test mode.</b> This is a stub payment portal — your submission will be logged for your landlord to review. Real card / ACH capture activates soon.</span>
      </div>

      {success ? (
        <div className="rugged-card p-6 space-y-2" data-testid="tenant-pay-success">
          <CheckCircle size={36} className="text-emerald-600" weight="fill"/>
          <div className="text-2xl font-black">Thank you, {payerName || d.tenant_name}.</div>
          <div className="text-sm text-slate-600">
            We logged a payment of <b>{money(success.reference ? amount * 100 : 0)}</b> at <b>{d.property_address}</b>{d.unit ? ` · Unit ${d.unit}` : ""}.
          </div>
          <div className="text-[11px] text-slate-500">Reference: <b>{success.reference}</b></div>
          <div className="text-sm text-slate-700">New balance: <b>{money(success.new_balance_cents)}</b></div>
          <div className="mt-4 text-[11px] text-slate-500">Your landlord will confirm receipt — a paper receipt or credit will follow within 1–2 business days.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-4 rugged-card space-y-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rent Account</div>
              <div className="text-sm font-black">{d.tenant_name}</div>
              <div className="text-xs text-slate-600">{d.property_address}{d.unit && ` · Unit ${d.unit}`}</div>
              <div className="text-xs text-slate-600">Monthly rent: <b>{money(d.monthly_rent_cents)}</b>, due day {d.due_day}</div>
            </div>
            <div className={`p-4 shadow-[3px_3px_0_#0f172a] ${d.balance_cents > 0 ? "bg-red-600" : "bg-emerald-600"} text-white`}>
              <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Current Balance</div>
              <div className="text-4xl font-black">{money(d.balance_cents)}</div>
              {d.is_delinquent && <div className="text-[11px] mt-1 opacity-90">{d.days_past_due} days past due · please pay to avoid escalation.</div>}
              {!d.is_delinquent && d.balance_cents === 0 && <div className="text-[11px] mt-1 opacity-90">Your account is current — thank you!</div>}
            </div>
          </div>

          <div className="mt-4 rugged-card p-4 space-y-3">
            <h2 className="text-lg font-black uppercase tracking-widest flex items-center gap-2">
              <CreditCard weight="fill" className="text-emerald-600"/> Make a Payment
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Amount ($)
                <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  data-testid="tp-amount" className="h-12 px-3 border-2 border-slate-300 text-lg font-black"/>
              </label>
              <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Method
                <select value={method} onChange={e => setMethod(e.target.value)} data-testid="tp-method"
                  className="h-12 px-3 border-2 border-slate-300 text-sm">
                  <option value="ach_stub">Bank transfer (ACH)</option>
                  <option value="card_stub">Credit / debit card</option>
                  <option value="check">I'm sending a check</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={payerName} onChange={e => setPayerName(e.target.value)}
                placeholder="Your name (if different from tenant)" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              <input value={payerEmail} onChange={e => setPayerEmail(e.target.value)}
                placeholder="Your email (for confirmation)" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder="Note (optional — e.g. 'partial rent, remainder Friday')"
              className="w-full p-2 border-2 border-slate-300 text-sm"/>
            <button onClick={submit} disabled={busy || !amount} data-testid="tp-submit"
              className="w-full h-14 bg-emerald-600 text-white font-black uppercase tracking-widest text-base shadow-[4px_4px_0_#0f172a] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? "Submitting…" : <>Submit Payment <ArrowRight size={18}/></>}
            </button>
            <div className="text-[10px] text-slate-500 flex items-center gap-1">
              <Bank size={12}/> {method === "check" ? "You'll ship the check separately — this just tells your landlord to expect it." : "STUB — no card/bank is charged. Your submission is logged for landlord review."}
            </div>
          </div>

          {d.history?.length > 0 && (
            <div className="mt-4 rugged-card p-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-2">Recent Payments</h3>
              <div className="space-y-1">
                {d.history.map((h, i) => (
                  <div key={i} className="text-xs flex items-center justify-between">
                    <span>{(h.created_at || "").slice(0, 10)} · {h.method}</span>
                    <b>{money(h.amount_cents)}</b>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

function Shell({ children, brand }) {
  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-slate-900 text-white p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {brand?.logo_path ? (
            <img src={`${API}/api/files/${brand.logo_path}`} alt={brand.name || "logo"} className="h-10 max-w-[140px] object-contain"/>
          ) : (
            <House weight="fill" size={30} className="text-white"/>
          )}
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Rent Payment Portal</div>
            <div className="text-lg font-black">{brand?.name || "Property Management"}</div>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-4">{children}</div>
      <div className="max-w-2xl mx-auto text-[10px] text-slate-500 p-4 text-center">
        Powered by Field Radar · secure magic link · this portal never asks for your password.
      </div>
    </div>
  );
}
